import { throttle } from 'es-toolkit';
import { PlayerStatus } from '../../constants';
export const initialPlayerManagerState = {
    currentGuideItem: null,
    playerStatus: PlayerStatus.IDLE,
    metadata: null,
    error: null,
    playbackRate: 1,
    volume: 0.5,
    currentTime: 0,
    duration: 0,
    playbackProgressHistory: {},
    isVideoAdLoaded: false,
};
export function createPlayerManagerStoreState(set, get) {
    return {
        ...initialPlayerManagerState,
        setCurrentGuideItem: (currentGuideItem, isLoading = true) => set({
            currentGuideItem,
            playerStatus: isLoading ? PlayerStatus.LOADING : PlayerStatus.IDLE,
            error: null,
            currentTime: 0,
            duration: 0,
        }),
        setPlayerStatus: (playerStatus) => set({ playerStatus }),
        setMetadata: (metadata) => set({ metadata }),
        setError: (error) => set({ error }),
        setPlaybackRate: (playbackRate) => set({ playbackRate }),
        setVolume: (volume) => set({ volume }),
        setCurrentTime: (currentTime) => {
            const { duration } = get();
            set({ currentTime: duration ? Math.min(currentTime, duration) : currentTime });
        },
        setDuration: (duration) => set({ duration }),
        cachePlaybackProgress: (guideId) => {
            const { playbackProgressHistory, metadata, currentTime, duration } = get();
            if (!guideId ||
                !duration ||
                !metadata ||
                metadata.isLive ||
                metadata.isAdBreak ||
                metadata.isLiveSeek) {
                return;
            }
            set({
                playbackProgressHistory: {
                    ...playbackProgressHistory,
                    [guideId]: {
                        time: currentTime,
                        percent: Math.round((currentTime / duration) * 100),
                    },
                },
            });
        },
        setIsVideoAdLoaded: (isVideoAdLoaded) => set({ isVideoAdLoaded }),
    };
}
export async function registerPlayerManagerEvents(player, playerStore) {
    const { setMetadata, setPlayerStatus, setError, setCurrentTime, setDuration, setPlaybackRate, setVolume, cachePlaybackProgress, setIsVideoAdLoaded, setCurrentGuideItem, } = playerStore;
    const throttledCachePlaybackProgress = throttle(cachePlaybackProgress, 
    // Represents 1% progress for a 10-minute duration, which seems like a good lower boundary.
    // Also, 1 operation every 6 seconds is reasonable for performance.
    6000);
    function onLoadStart() {
        setPlayerStatus(PlayerStatus.LOADING);
        setError(null);
        setIsVideoAdLoaded(false); // Might not be needed, but guarantees that the video dialog closes.
        if (!player.isLiveSeek()) {
            setCurrentTime(0);
            setDuration(0);
        }
        // Playback rate isn't always defined on loadstart if the player isn't in the DOM yet. Player Manager emits its own
        // `loadstart` event in load(), which can occur prior to the player being added to the DOM.
        if (player?.getPlaybackRate()) {
            setPlaybackRate(player?.getPlaybackRate());
        }
    }
    function onWaiting() {
        setPlayerStatus(PlayerStatus.WAITING);
    }
    function onPlaying() {
        setPlayerStatus(PlayerStatus.PLAYING);
    }
    function onIdle() {
        setPlayerStatus(PlayerStatus.IDLE);
        setCurrentTime(0);
        setDuration(0);
    }
    function onPause() {
        setPlayerStatus(PlayerStatus.IDLE);
    }
    function onEnded() {
        setPlayerStatus(PlayerStatus.IDLE);
        cachePlaybackProgress(player?.guideItem?.playableGuideId);
        setCurrentTime(0);
        setDuration(0);
    }
    function onError(event) {
        if (event.error.fatal && event.isLastSource) {
            setPlayerStatus(PlayerStatus.FAILED);
            setError(event);
        }
    }
    function onTimeUpdate() {
        const currentTime = player?.getCurrentTime() ?? 0;
        const duration = player?.getDuration() ?? 0;
        setCurrentTime(currentTime);
        setDuration(duration);
        if (duration) {
            throttledCachePlaybackProgress(player?.guideItem?.playableGuideId);
        }
    }
    function onRateChange() {
        setPlaybackRate(player?.getPlaybackRate());
    }
    function onVolumeChange() {
        setVolume(player?.getVolume());
    }
    function onSeeked() {
        setCurrentTime(player?.getCurrentTime());
        setDuration(player?.getDuration());
        cachePlaybackProgress(player?.guideItem?.playableGuideId);
    }
    function onNextGuideItem(guideItem) {
        setCurrentGuideItem(guideItem);
    }
    function onAutoplayGuideItem(guideItem) {
        setCurrentGuideItem(guideItem, false);
    }
    player.on('loadstart', onLoadStart);
    player.on('waiting', onWaiting);
    player.on('playing', onPlaying);
    player.on('pause', onPause);
    player.on('stopped', onIdle);
    player.on('ended', onEnded);
    player.on('error', onError);
    player.on('metadata', setMetadata);
    player.on('timeupdate', onTimeUpdate);
    player.on('ratechange', onRateChange);
    player.on('volumechange', onVolumeChange);
    player.on('seeked', onSeeked);
    player.on('nextguideitem', onNextGuideItem);
    player.on('autoplayguideitem', onAutoplayGuideItem);
    // AD EVENTS BELOW THIS LINE
    function onAdLoaded() {
        const videoContainerEl = player.adContainerElement;
        setIsVideoAdLoaded(videoContainerEl?.hasAttribute('data-video') || false);
    }
    function onAdSkipped(event) {
        if (event && 'adPosition' in event && event.adPosition === event.totalAds) {
            setIsVideoAdLoaded(false);
        }
        onAdFinished();
    }
    function onAdFinished() {
        setError(null);
        setCurrentTime(0);
        setDuration(0);
    }
    function onAllAdsComplete() {
        setIsVideoAdLoaded(false);
    }
    player.on('adloaded', onAdLoaded);
    player.on('adstart', onPlaying);
    player.on('adprogress', onTimeUpdate);
    player.on('adpause', onPause);
    player.on('adresume', onPlaying);
    player.on('adcomplete', onAdFinished);
    player.on('adskipped', onAdSkipped);
    player.on('alladscomplete', onAllAdsComplete);
    player.on('aderror', onAdFinished);
    player.on('advolumechange', onVolumeChange);
    player.on('advolumemuted', onVolumeChange);
}
